import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { SessionInfo } from '../shared/api.js';
import { apiRequest, ApiRequestError } from './api/client.js';

/**
 * The session, held in React state and nowhere else.
 *
 * The session id itself is never here: it is in an HttpOnly cookie the script
 * cannot read, which is the point. What this holds is the CSRF token, in memory,
 * for exactly as long as the tab is open. A reload asks the server for it again.
 *
 * `signOut` clears the state before the request resolves, so the moment the
 * operator clicks it the UI has already dropped everything it was showing —
 * including any Query Lab result on screen — rather than waiting for a round
 * trip that might fail.
 */

interface SessionState {
  session: SessionInfo | null;
  status: 'unknown' | 'signed-in' | 'signed-out';
  signIn: (password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Called when any request comes back 401, so one expiry signs the app out. */
  handleUnauthorized: () => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [status, setStatus] = useState<SessionState['status']>('unknown');

  const refresh = useCallback(async () => {
    try {
      const info = await apiRequest<SessionInfo>('/api/session');
      setSession(info);
      setStatus('signed-in');
    } catch {
      setSession(null);
      setStatus('signed-out');
    }
  }, []);

  const signIn = useCallback(async (password: string) => {
    const info = await apiRequest<SessionInfo>('/api/session', {
      method: 'POST',
      body: { password },
    });
    setSession(info);
    setStatus('signed-in');
  }, []);

  const signOut = useCallback(async () => {
    const token = session?.csrfToken;
    setSession(null);
    setStatus('signed-out');
    try {
      await apiRequest('/api/session', { method: 'DELETE', csrfToken: token });
    } catch (error) {
      // A session that is already gone is the outcome we wanted anyway.
      if (!(error instanceof ApiRequestError) || error.code !== 'unauthorized') throw error;
    }
  }, [session]);

  const handleUnauthorized = useCallback(() => {
    setSession(null);
    setStatus('signed-out');
  }, []);

  const value = useMemo<SessionState>(
    () => ({ session, status, signIn, signOut, handleUnauthorized, refresh }),
    [session, status, signIn, signOut, handleUnauthorized, refresh],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession must be used inside a SessionProvider');
  return value;
}
